import { IMetadataClient } from '../base-metadata-client';
import { AdfMetadataClient } from './adfmetadata-client';
import { Http } from '../http';
import { AuthService } from '../authentication/auth.service';
import { LoggerService } from 'app/logger/logger.service';
import { FactoryUrlInformationService } from '../factory-url-information.service';
import { A365MetadataClient } from './a365-metadata-client';
import { WorkspaceUrlInformationService } from '../workspace-url-information.service';
import { A365DataplaneClient } from './a365-dataplane-client';
import { Inject, Injectable } from '@angular/core';
import { GlobalVariables } from '../globals';
import { IFrameMessengerService } from '../iframe-messenger.service';
import { IFrameMetadataClient } from './iframemetadata-client';
import { A365WorkspaceCacheService } from './A365workspace-cache.service';
import { TridentIFrameMetadataClient } from './trident-iframemetadata-client';
import { IResourceContext, RESOURCE_CONTEXT_DATA } from 'app/multi-contexts/resource-context.typings';
import { LoggerHelper, buildLoggerHelperInContext } from 'app/logger/logger-helper';

@Injectable({
    providedIn: 'root'
})
export class MetadataClientFactoryService {
    private _dpMetadataClient: IMetadataClient;
    private _rpMetadataClient: IMetadataClient;
    private _iframeClient: IMetadataClient;
    private _loggerHelper: LoggerHelper = buildLoggerHelperInContext();

    constructor(
        @Inject(RESOURCE_CONTEXT_DATA) private readonly _contextData: IResourceContext,
        private readonly _logger: LoggerService,
        private readonly _httpClient: Http,
        private readonly _authService: AuthService,
        private readonly _adfUrlService: FactoryUrlInformationService,
        private readonly _workspaceUrlService: WorkspaceUrlInformationService,
        private readonly _workspaceCacheService: A365WorkspaceCacheService,
        private readonly _iframeMessengerService: IFrameMessengerService
    ) { }

    public getClient(dataPlaneForA365?: boolean, tridentClient?: boolean, contextData?: IResourceContext): IMetadataClient {
        if (tridentClient) {
            return this._getTridentIFrameClient(contextData);
        }
        if (GlobalVariables.enableIFrameMode) {
            if (__IS_FABRIC_DPE__) {
                return this._getTridentIFrameClient();
            } else if (__IS_FABRIC_ADF__) {
                return this._getAdfClient();
            } else if (__IS_FABRIC_MDF__) {
                return this._getTridentIFrameClient();
            } else {
                return this._getIFrameClient();
            }
        } else if (!__IS_A365__) {
            return this._getAdfClient();
        } else if (dataPlaneForA365) {
            return this._getDPClient();
        } else {
            return this._getRPClient();
        }
    }

    private _getRPClient(): IMetadataClient {
        if (!this._rpMetadataClient) {
            const rpMetadataClient = new A365MetadataClient(
                this._contextData,
                this._httpClient,
                this._authService,
                this._workspaceCacheService,
                this._workspaceUrlService,
                this._logger,
                this._loggerHelper
            );

            this._rpMetadataClient = rpMetadataClient;
            rpMetadataClient.dataplaneMetadataClient = this._getDPClient();
        }

        return this._rpMetadataClient;
    }

    private _getDPClient(): IMetadataClient {
        if (!this._dpMetadataClient) {
            this._dpMetadataClient = new A365DataplaneClient(
                this._contextData,
                this._httpClient,
                this._authService,
                this._workspaceUrlService,
                this._logger,
                this._loggerHelper,
                this._getRPClient() as A365MetadataClient,
                this._workspaceCacheService
            );
        }

        return this._dpMetadataClient;
    }

    private _getAdfClient(): IMetadataClient {
        if (!this._rpMetadataClient) {
            this._rpMetadataClient = new AdfMetadataClient(
                this._contextData,
                this._httpClient,
                this._authService,
                this._adfUrlService,
                this._logger,
                this._loggerHelper
            );
        }

        return this._rpMetadataClient;
    }

    private _getIFrameClient(): IMetadataClient {
        if (!this._iframeClient) {
            this._iframeClient = new IFrameMetadataClient(
                this._adfUrlService,
                this._logger,
                this._iframeMessengerService
            );
        }

        return this._iframeClient;
    }

    private _getTridentIFrameClient(contextData?: IResourceContext): IMetadataClient {
        if (!this._iframeClient) {
            this._iframeClient = new TridentIFrameMetadataClient(
                contextData ?? this._contextData,
                this._adfUrlService,
                this._logger,
                this._iframeMessengerService
            );
        }

        return this._iframeClient;
    }
}
